
import { useState, useEffect } from 'react';
import { Patient } from '@/types/doctor';
import { DateRange } from 'react-day-picker';
import { supabase } from '@/integrations/supabase/client';

type FilterStatus = 'all' | 'new' | 'in-treatment' | 'completed' | 'follow-up';

export const usePatients = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | {}>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch patients from database
  useEffect(() => {
    const fetchPatientsData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        console.log("Fetching patients data from database...");
        
        // Try to use the view we created in the SQL migration
        const { data: viewData, error: viewError } = await supabase
          .from('patient_profiles_view')
          .select('*');
          
        if (viewError) {
          console.warn("Patient profiles view not available, falling back to join query:", viewError);
          
          // Fall back to the join query if the view isn't available
          const { data: patientData, error: patientError } = await supabase
            .from('patients')
            .select(`
              id,
              date_of_birth,
              gender,
              address_street,
              address_city,
              address_state,
              address_country,
              medical_conditions,
              allergies,
              created_at,
              updated_at,
              status,
              progress,
              profile_id,
              profiles(id, first_name, last_name, avatar_url, preferred_language)
            `);
            
          if (patientError) {
            console.error("Error fetching patients:", patientError);
            throw patientError;
          }
          
          processPatientData(patientData);
        } else {
          console.log("Successfully fetched data from patient_profiles_view:", viewData);
          processViewData(viewData);
        }
        
        // Fetch treatment plans to supplement any missing progress data
        const { data: treatmentPlans, error: plansError } = await supabase
          .from('treatment_plans')
          .select('*');
          
        if (plansError) throw plansError;
        
        updatePatientsWithTreatmentData(treatmentPlans);
      } catch (err: any) {
        console.error('Error fetching patients:', err);
        setError('Failed to load patients data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPatientsData();
  }, []);

  // Process data from the patients table join with profiles
  const processPatientData = (patientData: any[]) => {
    if (!patientData) {
      console.warn("No patient data returned from database");
      setPatients([]);
      setFilteredPatients([]);
      return;
    }
    
    console.log("Processing patient data:", patientData);
    
    const formattedPatients: Patient[] = patientData.map(patient => {
      const profile = patient.profiles || {};
      
      // Format patient name from profile
      const firstName = profile.first_name || '';
      const lastName = profile.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      // Get condition from medical conditions if available
      let condition = 'Not specified';
      if (patient.medical_conditions && typeof patient.medical_conditions === 'object') {
        const conditions = Object.keys(patient.medical_conditions).filter(
          key => patient.medical_conditions[key] === true
        );
        if (conditions.length > 0) {
          condition = conditions.map(c => c.replace('has_', '').replace('_', ' ')).join(', ');
        }
      }
      
      return {
        id: patient.id,
        name: fullName || 'Unknown Patient',
        avatar: profile.avatar_url || null,
        condition: condition,
        created_at: patient.created_at,
        date: patient.created_at,
        progress: patient.progress,
        status: patient.status || 'new',
        profiles: {
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: '', // We don't have email in the profiles query
          phone_number: '', // We don't have phone in the profiles query
          avatar_url: profile.avatar_url
        }
      };
    });
    
    console.log("Formatted patients:", formattedPatients);
    setPatients(formattedPatients);
    setFilteredPatients(formattedPatients);
  };
  
  // Process data from the view we created in SQL
  const processViewData = (viewData: any[]) => {
    if (!viewData) {
      console.warn("No view data returned from database");
      setPatients([]);
      setFilteredPatients([]);
      return;
    }
    
    console.log("Processing view data:", viewData);
    
    const formattedPatients: Patient[] = viewData.map(patient => {
      // Format patient name from profile fields
      const firstName = patient.first_name || '';
      const lastName = patient.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      
      // Get condition from medical conditions if available
      let condition = 'Not specified';
      if (patient.medical_conditions && typeof patient.medical_conditions === 'object') {
        const conditions = Object.keys(patient.medical_conditions).filter(
          key => patient.medical_conditions[key] === true
        );
        if (conditions.length > 0) {
          condition = conditions.map(c => c.replace('has_', '').replace('_', ' ')).join(', ');
        }
      }
      
      return {
        id: patient.id,
        name: fullName || 'Unknown Patient',
        avatar: patient.avatar_url || null,
        condition: condition,
        created_at: patient.created_at,
        date: patient.created_at,
        progress: patient.progress,
        status: patient.status || 'new',
        profiles: {
          first_name: patient.first_name,
          last_name: patient.last_name,
          email: '', // We don't have email in the view
          phone_number: '', // We don't have phone in the view
          avatar_url: patient.avatar_url
        }
      };
    });
    
    console.log("Formatted patients from view:", formattedPatients);
    setPatients(formattedPatients);
    setFilteredPatients(formattedPatients);
  };
  
  // Update patients with treatment plan data
  const updatePatientsWithTreatmentData = (treatmentPlans: any[]) => {
    if (!treatmentPlans || treatmentPlans.length === 0) return;
    
    setPatients(prevPatients => {
      return prevPatients.map(patient => {
        // Skip if patient already has progress data
        if (patient.progress !== undefined) return patient;
        
        // Find treatment plans for this patient
        const patientPlans = treatmentPlans.filter(plan => plan.patient_id === patient.id);
        if (patientPlans.length === 0) {
          return { ...patient, progress: 0 };
        }
        
        // Calculate progress
        const completedPlans = patientPlans.filter(plan => plan.status === 'completed').length;
        const progress = Math.round((completedPlans / patientPlans.length) * 100);
        
        // Determine status if not set
        let status = patient.status;
        if (!status || status === 'new') {
          if (patientPlans.some(plan => plan.status === 'in-progress' || plan.status === 'in-treatment')) {
            status = 'in-treatment';
          } else if (patientPlans.every(plan => plan.status === 'completed')) {
            status = 'completed';
          } else if (patientPlans.some(plan => plan.status === 'follow-up')) {
            status = 'follow-up';
          }
        }
        
        return { ...patient, progress, status };
      });
    });
  };

  // Filter patients based on criteria
  useEffect(() => {
    let result = patients;
    
    if (statusFilter !== 'all') {
      result = result.filter(patient => {
        if (statusFilter === 'new') {
          return patient.status === 'new' || patient.status === 'pending';
        }
        if (statusFilter === 'in-treatment') {
          return patient.status === 'in-treatment' || patient.status === 'in-progress';
        }
        if (statusFilter === 'follow-up') {
          return patient.status === 'follow-up';
        }
        return patient.status === statusFilter;
      });
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(patient => 
        patient.name.toLowerCase().includes(term) || 
        (patient.condition && patient.condition.toLowerCase().includes(term))
      );
    }
    
    if ('from' in dateRange || 'to' in dateRange) {
      result = result.filter(patient => {
        if (!patient.date && !patient.created_at) return false;
        
        const patientDate = new Date(patient.date || patient.created_at);
        
        if ('from' in dateRange && 'to' in dateRange && dateRange.from && dateRange.to) {
          return patientDate >= dateRange.from && patientDate <= dateRange.to;
        } else if ('from' in dateRange && dateRange.from) {
          return patientDate >= dateRange.from;
        } else if ('to' in dateRange && dateRange.to) {
          return patientDate <= dateRange.to;
        }
        
        return true;
      });
    }
    
    setFilteredPatients(result);
  }, [patients, statusFilter, searchTerm, dateRange]);

  // Update patient status method
  const updatePatientStatus = async (patientId: string, status: 'new' | 'in-treatment' | 'completed' | 'follow-up') => {
    try {
      // Try to use our new function from the SQL migration
      const { data, error: fnError } = await supabase.rpc('update_patient_status', {
        patient_id: patientId,
        new_status: status
      });
      
      if (fnError) {
        console.warn("Function not available, falling back to regular update:", fnError);
        
        // Fall back to direct update if the function isn't available
        const { error: updateError } = await supabase
          .from('patients')
          .update({ status })
          .eq('id', patientId);
          
        if (updateError) throw updateError;
      }
      
      // Update local state
      setPatients(prevPatients => 
        prevPatients.map(patient => 
          patient.id === patientId ? { ...patient, status } : patient
        )
      );
      
      return true;
    } catch (err) {
      console.error('Error updating patient status:', err);
      return false;
    }
  };

  const totalPatients = patients.length;
  const activePatients = patients.filter(p => 
    p.status === 'new' || p.status === 'pending' || 
    p.status === 'in-treatment' || p.status === 'in-progress' ||
    p.status === 'follow-up'
  ).length;

  return {
    patients: filteredPatients,
    totalPatients,
    activePatients,
    statusFilter,
    setStatusFilter,
    searchTerm,
    setSearchTerm,
    dateRange,
    setDateRange,
    updatePatientStatus,
    isLoading,
    error
  };
};
